import { normalizeVisitorsReportItem, type VisitorsReportItem } from '@/api/reportsApi'
import { unwrapCentrifugePublication } from '@utils/unwrapCentrifugePublication'

export type VisitorsFeedKind = 'snapshot' | 'upsert' | 'leave' | 'count'

export type VisitorsFeedEvent = {
  type: string
  kind: VisitorsFeedKind
  activeNow: number | null
  items: VisitorsReportItem[] | null
  upsert: VisitorsReportItem | null
  removeIds: string[]
}

function clampActiveNow(v: unknown): number | null {
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (!Number.isFinite(n)) return null
    return Math.max(0, Math.trunc(n))
  }
  if (typeof v !== 'number' || !Number.isFinite(v)) return null
  return Math.max(0, Math.trunc(v))
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null
}

function readActiveNow(obj: Record<string, unknown> | null | undefined): number | null {
  if (!obj) return null
  return (
    clampActiveNow(obj.activeNow) ??
    clampActiveNow(obj.active_now) ??
    clampActiveNow(obj.onlineNow) ??
    clampActiveNow(obj.online_now) ??
    clampActiveNow(obj.onlineCount) ??
    clampActiveNow(obj.online_count) ??
    clampActiveNow(obj.visitorsOnline) ??
    clampActiveNow(obj.visitors_online) ??
    clampActiveNow(obj.visitorsCount) ??
    clampActiveNow(obj.visitors_count) ??
    clampActiveNow(obj.totalOnline) ??
    clampActiveNow(obj.total_online) ??
    clampActiveNow(obj.count) ??
    clampActiveNow(obj.total) ??
    clampActiveNow(obj.active) ??
    clampActiveNow(obj.online)
  )
}

function collectRecords(raw: unknown): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = []
  const seen = new Set<unknown>()
  const add = (value: unknown) => {
    const rec = asRecord(value)
    if (!rec || seen.has(rec)) return
    seen.add(rec)
    out.push(rec)
    add(rec.payload)
    add(rec.data)
    add(rec.summary)
    add(rec.result)
    add(rec.body)
    add(rec.stats)
    add(rec.state)
  }
  add(raw)
  add(unwrapCentrifugePublication(raw))
  return out
}

function readItems(obj: Record<string, unknown>): unknown[] | null {
  return (
    asArray(obj.items) ??
    asArray(obj.visitors) ??
    asArray(obj.sessions) ??
    asArray(obj.list) ??
    asArray(obj.online) ??
    asArray(obj.activeVisitors) ??
    asArray(obj.active_visitors)
  )
}

function readVisitorObject(obj: Record<string, unknown>): unknown {
  return obj.visitor ?? obj.session ?? obj.item ?? obj.row ?? null
}

function readRemoveIds(obj: Record<string, unknown>): string[] {
  const ids: string[] = []
  const push = (value: unknown) => {
    if (value == null || value === '') return
    const s = String(value).trim()
    if (s) ids.push(s)
  }
  push(obj.sessionId)
  push(obj.session_id)
  push(obj.visitorId)
  push(obj.visitor_id)
  push(obj.id)
  const nested = asRecord(readVisitorObject(obj))
  if (nested) {
    push(nested.sessionId)
    push(nested.session_id)
    push(nested.visitorId)
    push(nested.visitor_id)
    push(nested.id)
  }
  return [...new Set(ids)]
}

function eventTypeOf(records: Record<string, unknown>[]): string {
  for (const rec of records) {
    const t = rec.type ?? rec.event ?? rec.action
    if (typeof t === 'string' && t.trim()) return t.trim()
  }
  return ''
}

function classifyKind(type: string, hasManyItems: boolean): VisitorsFeedKind {
  const t = type.toLowerCase()
  if (
    t.includes('leave') ||
    t.includes('left') ||
    t.includes('offline') ||
    t.includes('exit') ||
    t.includes('removed')
  ) {
    return 'leave'
  }
  if (t.includes('snapshot') || t.includes('sync') || t.includes('state') || hasManyItems) {
    return 'snapshot'
  }
  if (
    t.includes('join') ||
    t.includes('online') ||
    t.includes('enter') ||
    t.includes('created') ||
    t.includes('upsert')
  ) {
    return 'upsert'
  }
  return hasManyItems ? 'snapshot' : 'count'
}

function normalizeItems(rawItems: unknown[] | null | undefined): VisitorsReportItem[] {
  if (!rawItems?.length) return []
  return rawItems
    .map((row) => normalizeVisitorsReportItem(row))
    .filter((row): row is VisitorsReportItem => row != null)
}

function activeNowFromItems(items: VisitorsReportItem[]): number {
  const online = items.reduce((n, row) => n + (row.isOnline ? 1 : 0), 0)
  return online > 0 ? online : items.length
}

/** JWT/proxy server-side channel: `operator.<id>.visitors.feed`. */
export function isOperatorVisitorsFeedChannel(channel: string): boolean {
  return /^operator\.\d+\.visitors(?:\.feed)?$/.test(String(channel ?? '').trim())
}

/** Full visitors.feed publication (count, snapshot, join/leave). */
export function parseVisitorsFeedEvent(raw: unknown): VisitorsFeedEvent | null {
  const records = collectRecords(raw)
  if (records.length === 0 && raw != null && typeof raw !== 'object') {
    const n = clampActiveNow(raw)
    if (n === null) return null
    return { type: '', kind: 'count', activeNow: n, items: null, upsert: null, removeIds: [] }
  }
  if (records.length === 0) return null

  const type = eventTypeOf(records)
  let activeNow: number | null = null
  let rawItems: unknown[] | null = null
  let rawVisitor: unknown = null
  let removeIds: string[] = []

  for (const rec of records) {
    activeNow ??= readActiveNow(rec)
    rawItems ??= readItems(rec)
    rawVisitor ??= readVisitorObject(rec)
    if (removeIds.length === 0) removeIds = readRemoveIds(rec)
  }

  const items = normalizeItems(rawItems)
  const upsert =
    normalizeVisitorsReportItem(rawVisitor) ??
    (items.length === 1 ? items[0] : null)
  const kind = classifyKind(type, items.length > 1)

  if (activeNow == null && items.length > 0 && kind !== 'leave') {
    activeNow = activeNowFromItems(items)
  }

  if (activeNow == null && upsert && kind === 'upsert' && upsert.isOnline) {
    /* count filled by store from previous + 1 */
  }

  const hasSignal =
    activeNow != null || items.length > 0 || upsert != null || (kind === 'leave' && removeIds.length > 0)
  if (!hasSignal) return null

  return {
    type,
    kind: items.length > 1 ? 'snapshot' : kind,
    activeNow,
    items: items.length > 0 ? items : null,
    upsert: kind === 'leave' ? null : upsert,
    removeIds: kind === 'leave' ? removeIds : [],
  }
}

/** WS operator.<id>.visitors.feed — visitors.active.updated (and snapshot variants). */
export function parseVisitorsActiveNowWsPayload(raw: unknown): number | null {
  return parseVisitorsFeedEvent(raw)?.activeNow ?? null
}

export function nextVisitorsActiveNow(
  current: number | null,
  ev: VisitorsFeedEvent,
  currentItems?: VisitorsReportItem[] | null,
): number {
  if (ev.activeNow != null) return ev.activeNow
  if (ev.kind === 'snapshot' && ev.items) {
    return activeNowFromItems(ev.items)
  }
  const prev = current ?? 0
  if (ev.kind === 'leave') {
    const ids = new Set(ev.removeIds)
    if (ids.size === 0) return Math.max(0, prev - 1)
    const known = currentItems ?? []
    if (known.length === 0) return Math.max(0, prev - 1)
    const wasOnline = known.some((row) => {
      if (!row.isOnline) return false
      const key = visitorFeedItemKey(row)
      return ids.has(key) || ids.has(row.sessionId) || ids.has(row.visitorId)
    })
    return wasOnline ? Math.max(0, prev - 1) : prev
  }
  if (ev.kind === 'upsert' && ev.upsert) {
    if (ev.upsert.isOnline === false) return prev
    const key = visitorFeedItemKey(ev.upsert)
    const existing = (currentItems ?? []).find((row) => visitorFeedItemKey(row) === key)
    if (existing?.isOnline) return prev
    return prev + 1
  }
  return prev
}

export function visitorFeedItemKey(row: Pick<VisitorsReportItem, 'sessionId' | 'visitorId'>): string {
  return String(row.sessionId || row.visitorId || '').trim()
}

export function mergeVisitorFeedItems(
  current: VisitorsReportItem[] | null | undefined,
  ev: VisitorsFeedEvent,
): VisitorsReportItem[] | null {
  if (ev.kind === 'snapshot' && ev.items) return ev.items

  const map = new Map<string, VisitorsReportItem>()
  for (const row of current ?? []) {
    const key = visitorFeedItemKey(row)
    if (key) map.set(key, row)
  }

  if (ev.kind === 'leave') {
    for (const id of ev.removeIds) {
      const existing = map.get(id)
      if (existing) {
        map.set(id, { ...existing, isOnline: false })
      } else {
        for (const [key, row] of map) {
          if (row.sessionId === id || row.visitorId === id) {
            map.set(key, { ...row, isOnline: false })
          }
        }
      }
    }
    return map.size > 0 ? [...map.values()] : current ?? null
  }

  const upserts = ev.items?.length ? ev.items : ev.upsert ? [ev.upsert] : []
  if (upserts.length === 0) return current ?? null
  for (const row of upserts) {
    const key = visitorFeedItemKey(row)
    if (!key) continue
    const prev = map.get(key)
    map.set(key, prev ? { ...prev, ...row } : row)
  }
  return [...map.values()]
}
